import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import { useAuth } from './useAuth';
import type { LinkPageStatsResponse } from '@/types';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';

export const useLinkPageStats = (linkpageId?: string, period: Period = 'month') => {
  const { user } = useAuth();
  const [stats, setStats] = useState<LinkPageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const getLinkPageStats = httpsCallable<
        { userId: string; period: Period; linkpageId?: string },
        LinkPageStatsResponse
      >(functions, 'getLinkPageStats');

      const result = await getLinkPageStats({
        userId: user.uid,
        period,
        linkpageId,
      });

      setStats(result.data);
    } catch (err) {
      console.error('Failed to fetch link page stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, [user, period, linkpageId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
